import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Check } from 'lucide-react';
import {
  applyConfirmedOrderFollowUp,
  changeConfirmedOrderItemSupplier,
  changeSupplierErrorMessage,
  poMovesMessage,
  deleteConfirmedOrderItem,
  dismissConfirmedOrderFollowUp,
  fetchConfirmedOrderById,
  followUpErrorMessage,
  linkConfirmedOrder,
  linkConfirmedOrderErrorMessage,
  orderQuantityChangeErrorMessage,
  reconcileConfirmedOrder,
  SHIP_PO_ACCEPT,
  updateConfirmedOrder,
  uploadShipPo,
  uploadShipPoErrorMessage,
  vesselUpdateErrorMessage,
  type ClientFollowUpDto,
  type ExtractedClientItemDto,
} from '../lib/confirmedOrderApi';
import { ClientFollowUpPanel, qtyText } from '../features/confirmedOrders/ClientFollowUpPanel';
import { OrderReadIssuesNote } from '../features/confirmedOrders/OrderReadIssuesNote';
import { emailLinesNotOnOrder, quantityChangesFrom, seedReconcileLines } from '../features/confirmedOrders/reconcileLines';
import { fetchQuotationList } from '../lib/quotationApi';
import {
  createVendorPurchaseOrdersErrorMessage,
  createVendorPurchaseOrdersFromConfirmedDetailed,
  missingVendorLinesNote,
  unresolvedVendorsGenerationNote,
  StockAllocation,
} from '../lib/vendorPurchaseOrderApi';
import VendorPoEmailsDialog from '../features/vendorPurchaseOrders/VendorPoEmailsDialog';
import ProcurementProposalModal from '../components/ProcurementProposalModal';
import { ChainRail } from '../features/traceability/ChainRail';
import { ModuleInfoButton } from '../components/ModuleInfoButton';
import LoadingState from '../components/LoadingState';
import { buildSupplierCanonicalizer } from '../utils/supplierDedup';
import { itemPoSendStatus, PO_STATUS_BADGE } from '../utils/poSendStatus';
import { formatDate as fmtDate, formatDateTime as fmtDateTime } from "../utils/dateFormat";
import { toCsv } from '../utils/csv';
import { exactCostText, unitCostTtd } from '../utils/pricing';
import { usdToTtd } from '../utils/fxDisplay';
import { orderLineCost, orderLineSelling, orderSectionTotals } from '../features/confirmedOrders/sectionTotals';
import { clientDisplayName } from '../utils/orderIdentity';
import { quotationVesselName } from '../utils/quotationVessel';
import { fetchConfirmedOrderSupplierOptions, type SupplierDropdownItem, type SupplierSource } from '../lib/vendorInvoiceApi';
import type { VendorOption } from '../lib/vendorsApi';
import { VendorSelect } from '../components/VendorSelect';
import { useToast } from '../context/ToastContext';
import { useConfirm } from '../components/ui/ConfirmProvider';
import { ColumnResizeGrip } from '../components/ColumnResizeGrip';
import { useColumnWidths } from '../hooks/useColumnWidths';
import { resolveTableLayout, type TableColumn } from '../utils/tableColumns';
import { SupplierSourcesNote } from '../features/quotations/SupplierSourcesNote';
import { currencyPrefix } from '../utils/priceUtils';
import BackArrow from '../components/ui/BackArrow';
import { StickerColorDot } from '../components/StickerColorDot';

const CONFIRMED_ORDER_DETAIL_INFO_CONTENT = (
  <div className="flex flex-col gap-3">
    <p>This is the detail view of a confirmed order (client acceptance of a quotation).</p>
    <p className="font-medium">You can:</p>
    <ul className="list-disc list-inside space-y-1 pl-1">
      <li>View RFQ ID, total quote, and line items</li>
      <li>
        Generate purchase orders in TT$ — one PO per vendor, from quotation line selected vendors. Use the system USD→TTD
        rate from settings.
      </li>
      <li>Drag a column's edge to resize it; your widths are saved. A column with nothing on any line is not shown.</li>
    </ul>
  </div>
);

const formatDate = (value?: string) => {
  return fmtDate(value);
};

// The label is the owner's (`priceUtils.currencyPrefix`,
// BUSINESS_RULES#quotation-screen-money-labels): "US$", never Intl's bare "$".
// The number stays this page's own: rounded to the cent, not ceiled — so not
// `formatCurrency`, whose ceil would move printed totals by a cent.
const formatMoney = (value?: number, currency?: string | null) => {
  if (typeof value !== 'number') return '--';
  const digits = Math.abs(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${value < 0 ? '-' : ''}${currencyPrefix(currency)}${digits}`;
};
const US = currencyPrefix('USD');

export const ConfirmedOrderDetailPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  // Retest C5: results, refusals and questions are said in the page. A browser alert or
  // confirm froze the tab until someone clicked it ("Generate purchase orders" reported
  // its result that way).
  const toast = useToast();
  const confirm = useConfirm();
  const columnWidths = useColumnWidths('confirmed-order-lines');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any>(null);
  const [creating, setCreating] = useState(false);
  // Buy-to-order: review what is already on the shelf before raising any PO.
  const [showStockProposal, setShowStockProposal] = useState(false);
  const [quotations, setQuotations] = useState<any[]>([]);
  const [linking, setLinking] = useState(false);
  const [selectedQuotationId, setSelectedQuotationId] = useState('');
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  // Vessel, editable in place. Deborah, 2026-09-04: an order is identified by
  // its ship, and until now there was no screen anywhere that let a person say
  // which ship — extraction never read one, so the field was blank on every
  // confirmed order in the system. Saving here writes at the RFQ (the
  // authority) and cascades to the quotation, this order and its purchase
  // orders in one transaction; see `vesselAssignment.service.js`.
  const [vesselDraft, setVesselDraft] = useState<string | null>(null);
  const [savingVessel, setSavingVessel] = useState(false);
  const [vesselError, setVesselError] = useState<string | null>(null);

  // B6 — the CLIENT's own purchase-order reference, editable in place beside the
  // vessel. Deborah, 2026-09-04: "they usually say make sure the purchase order
  // number is stated on the invoice, because when they have to verify or
  // validate our invoice, they have to tie it back with their purchase order."
  // It arrives with the client's acceptance, so it belongs on the order; the
  // client invoice inherits it at generation rather than being typed from
  // memory weeks later. A plain field, not a cascade: only the invoice copies it.
  const [poDraft, setPoDraft] = useState<string | null>(null);
  const [savingPo, setSavingPo] = useState(false);
  const [poError, setPoError] = useState<string | null>(null);

  // Reconciliation state
  const [reconciling, setReconciling] = useState(false);
  const [reconcileItems, setReconcileItems] = useState<any[]>([]);
  const [reconcileNote, setReconcileNote] = useState('');
  const [reconcileSaving, setReconcileSaving] = useState(false);
  // D45.3 — the client's later email the review is applying, or null for the order's own
  // reconciliation. Finalise then applies the email instead of reconciling.
  const [reconcileFollowUp, setReconcileFollowUp] = useState<ClientFollowUpDto | null>(null);
  const [followUpBusy, setFollowUpBusy] = useState<string | null>(null);
  const [followUpError, setFollowUpError] = useState<{ id: string; message: string } | null>(null);
  // What the ship's PO or email the review is seeded from wrote ([] for the order's own lines).
  const [reconcileRead, setReconcileRead] = useState<ExtractedClientItemDto[]>([]);

  // D81 rule 5 — a ship's PO that did not come by email, uploaded here.
  const shipPoInput = useRef<HTMLInputElement>(null);
  const [uploadingShipPo, setUploadingShipPo] = useState(false);
  const [shipPoError, setShipPoError] = useState<string | null>(null);

  // Mode 1 — send POs to vendors (batch)
  const [batchFormat, setBatchFormat] = useState<'pdf' | 'excel'>('pdf');
  const [emailPoIds, setEmailPoIds] = useState<string[] | null>(null);
  const [batchResult, setBatchResult] = useState<{ sent: number; skipped: number; failed: number } | null>(null);
  // D180: POs whose email went but could not be marked. Warned about and never offered again here, until a reload re-reads them.
  const [emailedNotMarked, setEmailedNotMarked] = useState<Array<{ id: string; label: string }>>([]);

  const confirmed = data?.confirmedOrder;
  const openFollowUps: ClientFollowUpDto[] = useMemo(
    () => ((data?.confirmedOrder?.clientFollowUps || []) as ClientFollowUpDto[]).filter((f) => f.status === 'open'),
    [data]
  );
  const confirmedItems = useMemo(() => {
    if (!data) return [];
    const items = data.confirmedOrder?.items;
    if (Array.isArray(items) && items.length) return items;
    return data.quotation?.items || [];
  }, [data]);

  // The rate `unitCostTtd` needs to convert a NON-pinned line's US$ cost to TT$
  // (BUSINESS_RULES#quotation-cost-ttd) — "US$ per 1 TT$", the inverse of the
  // quoted "TT$ per 1 US$" rate. Read off the linked quotation's own pinned
  // rate (`exchangeRate.usdToTtd`), already loaded onto this page by
  // `getConfirmedOrderDetail` — never a fresh Settings lookup, which could
  // disagree with the rate the line was actually costed at.
  //
  // A price-list-originated confirmed order (D54, TECH_DEBT #342) carries no
  // quotationId at all, so `data.quotation` is null and this is null too — the
  // page genuinely has no rate to reach for those lines. A TT$-pinned line
  // needs none (unitCostTtd returns the pin outright); a non-pinned line on
  // such an order reads Pending below rather than guessing a rate.
  const ttdToUsd = useMemo(() => {
    const usdToTtd = Number(data?.quotation?.exchangeRate?.usdToTtd);
    return Number.isFinite(usdToTtd) && usdToTtd > 0 ? 1 / usdToTtd : null;
  }, [data]);

  // All vendors across the order — shown as an order-level summary only ("Vendors on
  // lines" below). NOT a per-line dropdown source: a line's real options are whoever
  // has actually supplied THAT item, not whoever supplies anything on this order
  // (CO-02 — every line's dropdown was showing the whole order's vendor list).
  const vendorList = useMemo(() => {
    const set = new Set<string>();
    // From confirmed order vendors field
    (data?.confirmedOrder?.vendors || []).forEach((v: string) => { if (v) set.add(v); });
    // From each item's selectedVendor and vendorOptions
    confirmedItems.forEach((it: any) => {
      if (it.selectedVendor) set.add(it.selectedVendor);
      (Array.isArray(it.vendorOptions) ? it.vendorOptions : []).forEach((o: any) => {
        if (o?.vendorName) set.add(o.vendorName);
      });
    });
    return Array.from(set).sort();
  }, [confirmedItems, data?.confirmedOrder?.vendors]);

  // Per-item live supplier options, keyed by trimmed itemName — the same
  // four-collection lookup (`GET /api/suppliers/dropdown`, owner
  // `supplierOptions.service.js`) the backend already validates a supplier
  // change against (confirmedOrder.service.js:changeConfirmedOrderItemSupplier)
  // and the Quotation page's own supplier dropdown already uses. `item.vendorOptions`
  // is only ever populated by a code path gated off by default (TECH_DEBT #204), so
  // it is empty on every real line — this is the actual per-item source of truth (CO-02).
  const itemNames = useMemo(() => {
    const set = new Set<string>();
    confirmedItems.forEach((it: any) => {
      const n = String(it.itemName || '').trim();
      if (n) set.add(n);
    });
    return Array.from(set).sort();
  }, [confirmedItems]);
  const itemNamesKey = itemNames.join('');

  const [itemSupplierOptions, setItemSupplierOptions] = useState<Record<string, SupplierDropdownItem[]>>({});
  // D105.3: the vendor register from the same read, offered on every line after its priced
  // suppliers; null until it arrives.
  const [registeredVendors, setRegisteredVendors] = useState<VendorOption[] | null>(null);
  // D99.3: per item, the sources the server could not read; null = the whole lookup failed.
  const [itemSupplierUnread, setItemSupplierUnread] = useState<Record<string, SupplierSource[] | null>>({});

  // D104.20: ONE request for every line (the server reads the four sources once for the whole
  // order — it was one request per line, 38 on a 38-line order). The page renders first; the
  // options fill in when they arrive. A failed read marks every line "could not read", never empty.
  useEffect(() => {
    if (!id || itemNames.length === 0) {
      setItemSupplierOptions({});
      setItemSupplierUnread({});
      setRegisteredVendors([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { optionsByItem, unavailableSources, vendors } = await fetchConfirmedOrderSupplierOptions(id);
        if (cancelled) return;
        setItemSupplierOptions(optionsByItem);
        setItemSupplierUnread(Object.fromEntries(itemNames.map((n) => [n, unavailableSources])));
        setRegisteredVendors(vendors);
      } catch (err) {
        if (cancelled) return;
        console.warn('Failed to load supplier options for the order', err);
        setItemSupplierOptions({});
        setItemSupplierUnread(Object.fromEntries(itemNames.map((n) => [n, null])));
        // Each line already says "Could not read suppliers"; the picker then lists nothing.
        setRegisteredVendors([]);
      }
    })();
    return () => { cancelled = true; };
    // itemNamesKey is the stable identity of itemNames — depending on the array
    // itself would refetch whenever ANY field on the order changes (e.g. after a
    // supplier pick re-renders `data`), not just when the set of items changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, itemNamesKey]);

  // Collapse free-text supplier-name variants (case/spelling) to one canonical option so the
  // dropdown shows a single entry per real supplier. (QA Ticket C) Built from the order's own
  // vendor list PLUS every live per-item option fetched above, so a spelling variant coming
  // back from the four-collection lookup collapses the same way an order-native one does.
  const supplierCanonNames = useMemo(() => {
    const set = new Set<string>(vendorList);
    Object.values(itemSupplierOptions).forEach((opts) => {
      (opts || []).forEach((o) => { if (o?.vendorName) set.add(o.vendorName); });
    });
    return Array.from(set);
  }, [vendorList, itemSupplierOptions]);
  const supplierCanon = useMemo(() => buildSupplierCanonicalizer(supplierCanonNames), [supplierCanonNames]);

  const vendorPurchaseOrders = useMemo(() => {
    const list = data?.vendorPurchaseOrders;
    return Array.isArray(list) ? list : [];
  }, [data]);

  // PO generation's "a line got no PO at all" failure mode, computed server-side
  // (`vendorPurchaseOrder.service.computeMissingVendorLines`, reusing the same
  // `groupItemsByVendor` rule generation itself applies) so it is visible on a
  // reload, before anyone has generated a PO — not only right after someone clicks
  // Generate.
  const missingVendorLines = useMemo(() => {
    const list = data?.missingVendorLines;
    return Array.isArray(list) ? list : [];
  }, [data]);

  // B6 — the vendor invoices somebody tagged as bought FOR this order
  // (`VendorInvoice.assignment.confirmedOrderId`). Loaded on this response, so
  // the chain rail can state the hop instead of saying it was never loaded.
  const vendorInvoices = useMemo(() => {
    const list = data?.vendorInvoices;
    return Array.isArray(list) ? list : [];
  }, [data]);

  // A9 — grouped totals per tile + overall; A8 — markup % alert if <20%.
  // QUO-00241 / D104.21: a line's tile is its department by the owner's rule, so
  // every line is in exactly one tile and they add up to OVERALL — see
  // `features/confirmedOrders/sectionTotals`. D104.10: cost reads the exact cost, which an order
  // line does not store — the linked quotation's lines (already loaded here) state it.
  const quotationItems = data?.quotation?.items;
  const groupedTotals = useMemo(() => orderSectionTotals(confirmedItems, quotationItems), [confirmedItems, quotationItems]);

  // `quiet`: refresh in place after an action (no "Loading" screen, no jump to the
  // top, an unsaved notes edit kept); a failed quiet refresh says so, the page stays.
  const load = async ({ quiet = false }: { quiet?: boolean } = {}) => {
    if (!id) return;
    if (quiet) {
      try {
        setData(await fetchConfirmedOrderById(id));
      } catch {
        toast.error('The change was saved, but the page could not refresh. Reload to see the latest purchase orders.');
      }
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetchConfirmedOrderById(id);
      setData(res);
      setNotes(res.confirmedOrder?.notes || '');
    } catch (e: unknown) {
      // By code, never the server's prose (invariant 6).
      setError(
        (e as { code?: unknown } | null)?.code === 'CONFIRMED_ORDER_NOT_FOUND'
          ? 'This confirmed order could not be found. It may have been deleted.'
          : 'The confirmed order could not be loaded. Try again, and contact support if it keeps failing.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // D104.20: the list only feeds the manual-link picker, so it is read only when that picker shows.
  const needsManualLink = !!data && (data.confirmedOrder?.linkStatus === 'pending_manual' || !data.confirmedOrder?.quotationId);
  useEffect(() => {
    if (!needsManualLink) return;
    const loadQuotations = async () => {
      try {
        const res = await fetchQuotationList({ page: 1, limit: 200 });
        setQuotations(res.data.quotations || []);
      } catch {
        setQuotations([]);
      }
    };
    loadQuotations();
  }, [needsManualLink]);

  const handleCreatePo = async (regenerate = false, stockAllocations?: StockAllocation[]) => {
    if (!id || creating) return;
    if (regenerate) {
      const ok = await confirm({
        title: 'Regenerate purchase orders?',
        summary: 'This removes the existing purchase orders for this confirmed order and creates new ones from its current lines (TT$).',
        tone: 'error',
        confirmLabel: 'Regenerate',
      });
      if (!ok) return;
    }
    setCreating(true);
    try {
      const res = await createVendorPurchaseOrdersFromConfirmedDetailed(id, {
        regenerate,
        stockAllocations,
      });
      await load();

      const covered = res?.stock?.coveredFromStock || [];
      const partial = (res?.stock?.draw?.skipped || []).filter((s: any) => s.partial);
      const poCount = Array.isArray(res?.data) ? res.data.length : 0;
      // PO generation computes two failure modes and used to tell nobody: a PO
      // raised with no vendor record (unresolvedVendors) and a line that got
      // NO purchase order at all because its supplier was blank/pseudo
      // (missingVendors) — the more serious one, since that item silently
      // never gets ordered. Both are worded differently on purpose — they
      // need different fixes (pick a vendor on the PO vs. choose a supplier
      // on the line).
      const unresolvedVendors = res?.warnings?.unresolvedVendors || [];
      const missingVendorLinesFromGen = res?.warnings?.missingVendors || [];

      const parts: string[] = [];
      parts.push(
        poCount === 0
          ? 'No purchase orders needed — every line was filled from stock.'
          : regenerate
            ? `${poCount} purchase order(s) regenerated.`
            : `${poCount} purchase order(s) created in TT$ (one per vendor).`
      );
      const unresolvedNote = unresolvedVendorsGenerationNote(unresolvedVendors.length);
      if (unresolvedNote) parts.push(unresolvedNote);
      const missingNote = missingVendorLinesNote(missingVendorLinesFromGen);
      if (missingNote) parts.push(missingNote);
      if (covered.length) {
        const fully = covered.filter((c: any) => c.remainingToBuy <= 0).length;
        parts.push(
          `${covered.length} line(s) reduced using stock already held` +
            (fully ? `, ${fully} of them fully covered.` : '.')
        );
      }
      // Someone else's order took the stock between the proposal and this
      // click. Say so plainly — the PO covers the difference, but the buyer
      // asked for something they did not entirely get.
      if (partial.length) {
        parts.push(
          `Note: ${partial.length} line(s) could not reserve the full amount — ${partial
            .map((s: any) => s.reason)
            .join(' ')}`
        );
      }
      // In the page, never a browser alert: that froze the tab until someone clicked OK
      // (retest C5). The notes stay up longer than the one-line result.
      toast.success(parts[0]);
      if (parts.length > 1) toast.info(parts.slice(1).join('\n\n'), 12000);
    } catch (e: unknown) {
      // Copy keyed on the envelope's code (invariant 6), never the server's prose.
      toast.error(createVendorPurchaseOrdersErrorMessage(e));
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteItem = async (index: number) => {
    if (!id) return;
    const ok = await confirm({
      title: 'Delete this line from the order?',
      summary: 'This cannot be undone.',
      tone: 'error',
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    try {
      // D81 — the order version guards the position: a stale page is refused, never the wrong line.
      const res = await deleteConfirmedOrderItem(id, index, String(data?.confirmedOrder?.updatedAt || ''));
      setData((prev: any) => ({ ...prev, confirmedOrder: res }));
    } catch (e: unknown) {
      toast.error(orderQuantityChangeErrorMessage(e));
    }
  };

  const handleLink = async () => {
    if (!id || !selectedQuotationId) return;
    setLinking(true);
    try {
      const res = await linkConfirmedOrder(id, selectedQuotationId);
      // D157.3 — the link can rename the vessel: drop the old colour; the re-read brings the new
      // vessel's colour and the linked quotation and RFQ.
      setData((prev: any) => ({ ...prev, confirmedOrder: res, vesselStickerColor: null }));
      setSelectedQuotationId('');
      toast.success('Confirmed order linked to quotation.');
      await load({ quiet: true });
    } catch (e: unknown) {
      toast.error(linkConfirmedOrderErrorMessage(e));
    } finally {
      setLinking(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!id) return;
    setSavingNotes(true);
    try {
      const res = await updateConfirmedOrder(id, { notes });
      setData((prev: any) => ({ ...prev, confirmedOrder: res }));
    } catch {
      // No server prose on screen (invariant 6); a plain whitelisted `$set`, so the copy says the
      // one thing that matters: nothing was stored.
      toast.error('The notes could not be saved. Nothing has been changed — try again.');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleSaveVessel = async () => {
    if (!id || vesselDraft === null) return;
    setSavingVessel(true);
    setVesselError(null);
    try {
      const res = await updateConfirmedOrder(id, { vesselName: vesselDraft });
      // D157.3 — the old vessel's sticker colour goes now; the re-read brings the new vessel's.
      setData((prev: any) => ({ ...prev, confirmedOrder: res, vesselStickerColor: null }));
      setVesselDraft(null);
      await load({ quiet: true });
    } catch (e: unknown) {
      // Copy chosen by the envelope's `code`, never the server's prose
      // (invariant 6) — and it must say that nothing was stored, because the
      // write spans four collections and a person who typed a ship name needs
      // to know whether it took.
      setVesselError(vesselUpdateErrorMessage(e));
    } finally {
      setSavingVessel(false);
    }
  };

  const handleSavePo = async () => {
    if (!id || poDraft === null) return;
    setSavingPo(true);
    setPoError(null);
    try {
      const res = await updateConfirmedOrder(id, { clientPoNumber: poDraft.trim() });
      setData((prev: any) => ({ ...prev, confirmedOrder: res }));
      setPoDraft(null);
    } catch {
      // No server prose on screen (invariant 6). There is no coded failure
      // specific to this field — it is a plain whitelisted `$set` — so the copy
      // says the one thing that matters: nothing was stored.
      setPoError('The client PO number could not be saved. Nothing has been changed — try again.');
    } finally {
      setSavingPo(false);
    }
  };

  // Review & Finalise, seeded with what the ship's PO or email wrote (`read`; [] = the order's own
  // lines). Which lines start ticked is reconcileLines.startsTicked (D81 rule 3). With a follow-up,
  // Finalise applies that email instead of reconciling (D45.3).
  const openReview = (read: ExtractedClientItemDto[], followUp: ClientFollowUpDto | null) => {
    setReconcileItems(seedReconcileLines(confirmedItems, read));
    setReconcileRead(read);
    setReconcileNote('');
    setReconcileFollowUp(followUp);
    setReconciling(true);
  };

  const handleApplyFollowUp = (followUp: ClientFollowUpDto) => {
    setFollowUpError(null);
    openReview(followUp.extractedItems || [], followUp);
  };

  // D81 — Change quantities on EVERY order. A read still waiting for review is what it offers
  // (the same table as Review & Finalise); on a settled order it is seeded from the order.
  const handleChangeQuantities = () =>
    openReview(confirmed?.reconciliationStatus === 'pending' ? confirmed.extractedClientItems || [] : [], null);

  const handleShipPoChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Cleared, so choosing the same file again (after a refusal) fires again.
    e.target.value = '';
    if (!id || !file) return;
    setUploadingShipPo(true);
    setShipPoError(null);
    try {
      await uploadShipPo(id, file);
      // The read loads the uploader's name for the new follow-up.
      await load();
    } catch (err: unknown) {
      setShipPoError(uploadShipPoErrorMessage(err));
    } finally {
      setUploadingShipPo(false);
    }
  };

  const handleDismissFollowUp = async (followUp: ClientFollowUpDto) => {
    if (!id) return;
    setFollowUpBusy(followUp._id);
    setFollowUpError(null);
    try {
      const res = await dismissConfirmedOrderFollowUp(id, followUp._id);
      setData((prev: any) => ({ ...prev, confirmedOrder: res }));
    } catch (e: unknown) {
      setFollowUpError({ id: followUp._id, message: followUpErrorMessage(e) });
    } finally {
      setFollowUpBusy(null);
    }
  };

  const exportOrder = () => {
    const items = confirmedItems;
    const header = ['Item', 'Description', 'Qty', 'Unit', 'Unit Price', 'Total Price', 'Vendor', 'Category'];
    const lines: Array<Array<string | number | null | undefined>> = items.map((it: any) => [
      it.itemName || '',
      it.itemDescription || '',
      it.quantity ?? '',
      it.uom || '',
      it.unitPrice ?? '',
      it.totalPrice ?? '',
      it.selectedVendor || '',
      it.category || '',
    ]);

    // toCsv, not a private escaper: the old one doubled quotes and left a
    // vendor name or category beginning "=" executable when the export is
    // opened. See utils/csv. (TECH_DEBT #38)
    const csv = toCsv([header, ...lines]);

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `confirmed_order_${confirmed?.quotationNumber || confirmed?._id || 'export'}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="h-full"><LoadingState text="Loading order..." /></div>;
  }

  if (error || !data) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-red-600">
        {error || 'Confirmed order not found'}
      </div>
    );
  }

  // D172.4: the selling figures the tiles sum (`orderLineSelling`); TT$ at the linked quotation's pinned rate
  // through `usdToTtd` (the total converted, not unit x qty). Worked out once: the column test and the cells read it.
  const sellingByLine = confirmedItems.map((it: any) => {
    const selling = orderLineSelling(it);
    return {
      unit: selling?.unit,
      total: selling?.total,
      unitTtd: selling ? usdToTtd(selling.unit, ttdToUsd) : null,
      totalTtd: selling ? usdToTtd(selling.total, ttdToUsd) : null,
    };
  });
  const sellMoney = (v: number | null | undefined, cur: 'USD' | 'TTD') =>
    typeof v === 'number' ? `${currencyPrefix(cur)}${v.toFixed(2)}` : '';
  // D178: a column nothing on any line would fill is not drawn. The four selling columns hide together, so "no
  // rate" (D172.4: TT$ cells blank) never reads as "no selling price"; Category, Remarks and Internal Remarks
  // each decide alone, from the field, not the printed text (Category prints '--' when blank).
  const anyText = (field: string) => confirmedItems.some((it: any) => String(it[field] ?? '').trim() !== '');
  const show = {
    selling: sellingByLine.some((line: any) => Object.values(line).some((v) => typeof v === 'number')),
    category: anyText('category'),
    remarks: anyText('remarks'),
    internalRemarks: anyText('internalRemarks'),
  };
  // One list drives col and th (the td cells stay hand-written, `show.<flag> &&` in step). The first three
  // never hide: `frozen-cols-3` pins by position. D172.4 (Deborah): selling per line; QA retest item 10:
  // Total Cost is cost x qty, not what the client pays. D172.3: the staff-only remark has its own column.
  const lineColumns: Array<TableColumn & { header: string; visible: boolean }> = [
    { key: 'number', header: '#', width: 64, visible: true },
    { key: 'uom', header: 'UOM', width: 96, visible: true },
    { key: 'item', header: 'Item Name / Description', width: null, visible: true },
    { key: 'qty', header: 'Qty', width: 72, visible: true },
    { key: 'usCost', header: 'US$ Cost', width: 130, visible: true },
    { key: 'ttCost', header: 'TT$ Cost', width: 130, visible: true },
    { key: 'totalCost', header: 'Total Cost', width: 130, visible: true },
    { key: 'unitSellUsd', header: 'Unit Selling (US$)', width: 150, visible: show.selling },
    { key: 'totalSellUsd', header: 'Selling Total (US$)', width: 150, visible: show.selling },
    { key: 'unitSellTtd', header: 'Unit Selling (TT$)', width: 150, visible: show.selling },
    { key: 'totalSellTtd', header: 'Selling Total (TT$)', width: 150, visible: show.selling },
    { key: 'supplier', header: 'Supplier & Date', width: 240, visible: true },
    { key: 'poStatus', header: 'PO Status', width: 170, visible: true },
    { key: 'category', header: 'Category', width: 120, visible: show.category },
    { key: 'remarks', header: 'Remarks', width: 200, visible: show.remarks },
    { key: 'internalRemarks', header: 'Internal Remarks', width: 200, visible: show.internalRemarks },
    { key: 'actions', header: 'Actions', width: 80, visible: true },
  ].filter((c) => c.visible);
  const lineLayout = resolveTableLayout(lineColumns, columnWidths.widths);
  const numberWidth = lineLayout.widths.number as number;
  const uomWidth = lineLayout.widths.uom as number;

  return (
    <div className="h-full flex flex-col bg-brand-bg">
      <div className="shrink-0 border-b border-slate-200 bg-brand-bg px-4 pb-3 pt-4 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-col gap-1 shrink-0">
            <div className="flex items-center gap-2">
              <BackArrow label="Back to confirmed orders" onClick={() => navigate('/confirmed-orders')} />
              <h1 className="text-xl font-semibold leading-7 text-slate-900">{confirmed?.quotationNumber || 'Confirmed Order'}</h1>
              <ModuleInfoButton content={CONFIRMED_ORDER_DETAIL_INFO_CONTENT} ariaLabel="Confirmed order detail information" />
            </div>
            <div className="text-xs text-slate-500 flex flex-wrap items-center gap-x-1 gap-y-1">
              {/* The client name, not the raw From header this used to print. */}
              <span>Client: {clientDisplayName(confirmed?.clientName) || '--'}</span>
              <span>·</span>
              {vesselDraft === null ? (
                <>
                  <span>Vessel: {confirmed?.vesselName || '--'}</span>
                  <StickerColorDot color={data?.vesselStickerColor} />
                  <button
                    type="button"
                    onClick={() => { setVesselError(null); setVesselDraft(confirmed?.vesselName || ''); }}
                    className="text-primary-500 hover:underline font-semibold"
                  >
                    {confirmed?.vesselName ? 'Edit' : 'Add vessel'}
                  </button>
                </>
              ) : (
                <span className="inline-flex items-center gap-1">
                  <span>Vessel:</span>
                  <input
                    autoFocus
                    value={vesselDraft}
                    onChange={(e) => setVesselDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveVessel();
                      if (e.key === 'Escape') { setVesselDraft(null); setVesselError(null); }
                    }}
                    placeholder="e.g. MV NORDIC"
                    className="h-7 w-48 rounded-md border border-slate-300 px-2 text-xs focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                  <button
                    type="button"
                    onClick={handleSaveVessel}
                    disabled={savingVessel}
                    className="h-7 px-2 rounded-md bg-primary-500 text-white text-xs font-semibold disabled:opacity-60"
                  >
                    {savingVessel ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setVesselDraft(null); setVesselError(null); }}
                    className="h-7 px-2 rounded-md border border-slate-300 text-xs font-semibold text-slate-600"
                  >
                    Cancel
                  </button>
                </span>
              )}
              <span>·</span>
              {/* B6 — the number the client will check our invoice against. */}
              {poDraft === null ? (
                <>
                  <span>Client PO: {confirmed?.clientPoNumber || '--'}</span>
                  <button
                    type="button"
                    onClick={() => { setPoError(null); setPoDraft(confirmed?.clientPoNumber || ''); }}
                    className="text-primary-500 hover:underline font-semibold"
                  >
                    {confirmed?.clientPoNumber ? 'Edit' : 'Add client PO'}
                  </button>
                </>
              ) : (
                <span className="inline-flex items-center gap-1">
                  <span>Client PO:</span>
                  <input
                    autoFocus
                    value={poDraft}
                    onChange={(e) => setPoDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSavePo();
                      if (e.key === 'Escape') { setPoDraft(null); setPoError(null); }
                    }}
                    placeholder="the client's PO reference"
                    className="h-7 w-48 rounded-md border border-slate-300 px-2 text-xs focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                  <button
                    type="button"
                    onClick={handleSavePo}
                    disabled={savingPo}
                    className="h-7 px-2 rounded-md bg-primary-500 text-white text-xs font-semibold disabled:opacity-60"
                  >
                    {savingPo ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setPoDraft(null); setPoError(null); }}
                    className="h-7 px-2 rounded-md border border-slate-300 text-xs font-semibold text-slate-600"
                  >
                    Cancel
                  </button>
                </span>
              )}
              <span>·</span>
              <span>Created: {formatDate(confirmed?.confirmedAt)}</span>
            </div>
            {vesselError && (
              <p className="text-xs text-rose-600" role="alert">{vesselError}</p>
            )}
            {poError && (
              <p className="text-xs text-rose-600" role="alert">{poError}</p>
            )}
            {shipPoError && (
              <p className="text-xs text-rose-600" role="alert">{shipPoError}</p>
            )}
            {/*
              `vendorPurchaseOrders` comes back on this response, so the PO hop
              is stated rather than guessed: an order with none raised shows the
              gap. B6 added `vendorInvoices` beside it — the supplier bills
              somebody tagged as bought FOR this order — so that hop is now
              stated too. The client invoice is still NOT loaded here and stays
              deliberately unmentioned, so the rail says "not loaded" for it
              rather than claiming it does not exist.
            */}
            <ChainRail
              current="confirmedOrder"
              rfq={confirmed?.rfqId}
              rfqNumber={confirmed?.rfqNumber}
              quotation={confirmed?.quotationId}
              quotationNumber={confirmed?.quotationNumber}
              confirmedOrder={confirmed?._id}
              purchaseOrders={vendorPurchaseOrders}
              vendorInvoices={vendorInvoices}
              className="mt-1"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportOrder}
              className="px-3 py-2 rounded-md border border-slate-200 bg-white text-xs font-semibold text-slate-700"
            >
              Export Order
            </button>
            <button
              onClick={handleChangeQuantities}
              disabled={reconciling}
              className="px-3 py-2 rounded-md border border-slate-200 bg-white text-xs font-semibold text-slate-700 disabled:opacity-60"
            >
              Change quantities
            </button>
            {/* D81 rule 5 — the ship's PO that came by WhatsApp or a portal, read like an emailed one. */}
            <input
              ref={shipPoInput}
              type="file"
              accept={SHIP_PO_ACCEPT}
              aria-label="Ship's PO file"
              className="hidden"
              onChange={handleShipPoChosen}
            />
            <button
              onClick={() => shipPoInput.current?.click()}
              disabled={uploadingShipPo}
              className="px-3 py-2 rounded-md border border-slate-200 bg-white text-xs font-semibold text-slate-700 disabled:opacity-60"
            >
              {uploadingShipPo ? 'Uploading…' : "Upload ship's PO"}
            </button>
            <button
              onClick={() => navigate(`/confirmed-orders/${id}/invoice`)}
              className="px-3 py-2 rounded-md border border-primary-500 bg-white text-xs font-semibold text-primary-600"
            >
              Create invoice
            </button>
            {/* Buy-to-order: the residual from an earlier over-purchase is only
                worth anything if someone looks before buying again. */}
            {vendorPurchaseOrders.length === 0 && (
              <button
                onClick={() => setShowStockProposal(true)}
                className="px-3 py-2 rounded-md border border-emerald-300 bg-emerald-50 text-emerald-900 text-xs font-semibold disabled:opacity-60"
                disabled={creating}
                title="Check what this order can be filled from stock already held, before raising POs"
              >
                Check stock first
              </button>
            )}
            {vendorPurchaseOrders.length > 0 ? (
              // A badge, as on the Confirmed Orders list: creating POs for an order that has
              // them returns the ones it has and writes nothing, so a button here did nothing
              // and then reported "N purchase order(s) created" (QA 2026-10-12).
              // Regenerate POs, beside it, is the way to rebuild them.
              <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800">
                <Check aria-hidden="true" className="h-3.5 w-3.5" />
                POs created
              </span>
            ) : (
              <button
                onClick={() => handleCreatePo(false)}
                className="px-3 py-2 rounded-md bg-primary-500 text-white text-xs font-semibold disabled:opacity-60"
                disabled={creating}
              >
                {creating ? 'Working...' : 'Generate purchase orders (TT$)'}
              </button>
            )}
            {vendorPurchaseOrders.length > 0 && (
              <button
                onClick={() => handleCreatePo(true)}
                className="px-3 py-2 rounded-md border border-amber-300 bg-amber-50 text-amber-900 text-xs font-semibold disabled:opacity-60"
                disabled={creating}
                title="Delete existing POs for this order and regenerate from current lines"
              >
                Regenerate POs
              </button>
            )}
          </div>
          {vendorPurchaseOrders.length > 0 && (
            <p className="mt-1 max-w-md text-xs text-slate-500">
              {/* D104.22 — the server's rule (vendorPurchaseOrder.service refuseRegenerateWhileLocked). */}
              Regenerate deletes this order's purchase orders and creates new ones from the current lines.
              It is refused if any of them has been sent to the supplier or is on a vendor or client invoice.
            </p>
          )}
        </div>
      </div>

      <div className="flex-1 px-8 py-4 overflow-y-auto">
        <div className="bg-white border border-slate-200 rounded-md shadow-sm flex flex-col overflow-hidden">
          <div className="p-4 border-b border-slate-200 grid grid-cols-1 md:grid-cols-3 gap-4 text-xs text-slate-600">
            <div>
              <div className="text-xs text-slate-400">RFQ ID</div>
              <div className="text-sm text-slate-800">{confirmed?.rfqNumber || '--'}</div>
            </div>
            <div>
              <div className="text-xs text-slate-400">Total Quote</div>
              <div className="text-sm text-slate-800">
                {formatMoney(confirmed?.totalQuote, confirmed?.currency)}
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-400">Total Items</div>
              <div className="text-sm text-slate-800">{confirmed?.totalItems ?? 0}</div>
            </div>
          </div>

          {(confirmed?.linkStatus === 'pending_manual' || !confirmed?.quotationId) && (
            <div className="p-4 border-b border-slate-200 bg-slate-50">
              <div className="text-xs font-semibold text-slate-700 mb-2">Manual Link Required</div>
              <div className="flex items-center gap-2">
                <select
                  value={selectedQuotationId}
                  onChange={(e) => setSelectedQuotationId(e.target.value)}
                  className="h-9 rounded-md border border-slate-200 bg-white px-3 text-xs min-w-[320px]"
                >
                  <option value="">Select quotation</option>
                  {quotations.map((q) => (
                    <option key={q._id} value={q._id}>
                      {q.quotationNumber} • {q.customer?.customerName || '--'} • {quotationVesselName(q) || '--'}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleLink}
                  disabled={linking || !selectedQuotationId}
                  className="px-3 py-2 rounded-md bg-primary-500 text-white text-xs font-semibold disabled:opacity-60"
                >
                  {linking ? 'Linking...' : 'Link'}
                </button>
              </div>
            </div>
          )}

          {/* Persists across a reload — computed server-side off the CO's current
              lines (`missingVendorLines`), not off anything generation left behind,
              so whoever opens this order tomorrow sees it too, not only whoever
              generated the PO. See `missingVendorLinesNote` for why this uses the
              exact same wording as the post-generation toast. */}
          {missingVendorLines.length > 0 && (
            <div className="p-4 border-b border-amber-200 bg-amber-50">
              <div className="text-sm font-semibold text-amber-800 mb-1">
                ⚠️ {missingVendorLines.length} line{missingVendorLines.length === 1 ? '' : 's'} will not be ordered
              </div>
              <div className="text-xs text-amber-700">{missingVendorLinesNote(missingVendorLines)}</div>
            </div>
          )}

          {/* ── Reconciliation banner ── */}
          {confirmed?.reconciliationStatus === 'pending' && !reconciling && (
            <div className="p-4 border-b border-amber-200 bg-amber-50">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm font-semibold text-amber-800 mb-1">
                    ⚠️ Reconciliation Required
                  </div>
                  <div className="text-xs text-amber-700">
                    AI extracted <strong>{(confirmed.extractedClientItems || []).length} item(s)</strong> from the client's confirmation email.
                    These may differ from your quotation. Please review and finalise the confirmed quantities.
                  </div>
                </div>
                <button
                  onClick={() => openReview(confirmed.extractedClientItems || [], null)}
                  className="shrink-0 px-4 py-2 rounded-md bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700"
                >
                  Review & Finalise
                </button>
              </div>
            </div>
          )}

          {/* D45.8 — what the read of the PO could not read. */}
          {(confirmed?.extractionReadIssues || []).length > 0 && (
            <div className="px-4 py-2 border-b border-slate-200">
              <OrderReadIssuesNote issues={confirmed.extractionReadIssues} />
            </div>
          )}

          {/* D45.3 — later emails about this order, waiting for a person. */}
          {!reconciling &&
            openFollowUps.map((followUp) => (
              <ClientFollowUpPanel
                key={followUp._id}
                followUp={followUp}
                orderLines={confirmedItems}
                onApply={handleApplyFollowUp}
                onDismiss={handleDismissFollowUp}
                busy={followUpBusy === followUp._id}
                error={followUpError?.id === followUp._id ? followUpError.message : null}
              />
            ))}

          {confirmed?.reconciliationStatus === 'done' && (
            <div className="px-4 py-2 border-b border-cyan-100 bg-cyan-50 text-xs text-cyan-700 flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              Order reconciled — items and quantities reflect client's actual confirmation.
              {confirmed.reconciledAt && (
                <span className="text-cyan-500">({fmtDateTime(confirmed.reconciledAt)})</span>
              )}
            </div>
          )}

          {/* ── Reconciliation review table ── */}
          {reconciling && (
            <div className="p-4 border-b border-amber-200 bg-amber-50 space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-amber-900">
                  {reconcileFollowUp ? 'Review the client’s changes' : 'Review Confirmed Items'}
                </div>
                <button
                  onClick={() => { setReconciling(false); setReconcileFollowUp(null); }}
                  className="text-xs text-slate-500 hover:underline"
                >
                  Cancel
                </button>
              </div>
              <div className="text-xs text-amber-700 mb-2">
                Highlighted = matches quotation · Amber = quantity changed · Red = not in the ship's order (unchecked = removed)
              </div>

              <div className="overflow-auto rounded-lg border border-amber-200 bg-white">
                <table className="data-table-grid w-full text-xs">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-3 py-2 text-left">Include</th>
                      <th className="px-3 py-2 text-left">#</th>
                      <th className="px-3 py-2 text-left">Item</th>
                      <th className="px-3 py-2 text-right">Quoted Qty</th>
                      <th className="px-3 py-2 text-right">Ship's Qty</th>
                      <th className="px-3 py-2 text-right">Final Qty</th>
                      <th className="px-3 py-2 text-left">Unit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reconcileItems.map((item: any, idx: number) => {
                      const changed = item._clientQty !== null && item._clientQty !== item._quotedQty;
                      const notInClient = item._clientQty === null;
                      const rowColor = !item._keep ? 'opacity-40' : changed ? 'bg-amber-50' : notInClient ? 'bg-rose-50' : 'bg-cyan-50/30';
                      return (
                        <tr key={idx} className={`border-b border-slate-100 ${rowColor}`}>
                          <td className="px-3 py-2">
                            <input type="checkbox" checked={!!item._keep}
                              onChange={(e) => {
                                const next = [...reconcileItems];
                                next[idx] = { ...next[idx], _keep: e.target.checked };
                                setReconcileItems(next);
                              }}
                              className="h-3.5 w-3.5 accent-cyan-600"
                            />
                          </td>
                          <td className="px-3 py-2 text-slate-400">{item.lineNumber ?? idx + 1}</td>
                          <td className="px-3 py-2 font-medium text-slate-800 max-w-xs truncate">{item.itemName || '--'}</td>
                          <td className="px-3 py-2 text-right text-slate-500">{item._quotedQty ?? '--'}</td>
                          <td className={`px-3 py-2 text-right font-semibold ${notInClient ? 'text-rose-400' : changed ? 'text-amber-600' : 'text-cyan-600'}`}>
                            {item._clientQty ?? '—'}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <input type="number" min={0} value={item.quantity ?? ''}
                              onChange={(e) => {
                                const next = [...reconcileItems];
                                // Blank stays blank (null), never 0 — D81 / invariant 8.
                                next[idx] = { ...next[idx], quantity: e.target.value === '' ? null : Number(e.target.value) };
                                setReconcileItems(next);
                              }}
                              className="w-20 h-6 rounded border border-slate-200 px-2 text-right text-xs focus:outline-none focus:ring-1 focus:ring-primary-500"
                            />
                          </td>
                          <td className="px-3 py-2 text-slate-500">{item.uom || item.unit || '--'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* D81 — a ship's line matching no quoted line cannot be added here: it is priced on
                  the quotation first. */}
              {(() => {
                const notQuoted = emailLinesNotOnOrder(confirmedItems, reconcileRead);
                if (!notQuoted.length) return null;
                return (
                  <section aria-label="Not quoted" className="text-xs text-amber-900">
                    <div className="font-semibold">Not quoted — add it to the quotation first</div>
                    <ul className="mt-0.5 list-disc pl-4 space-y-0.5">
                      {notQuoted.map((item, i) => (
                        <li key={item._id || i}>
                          {item.itemName} — {qtyText(item.quantity, item.uom)}
                        </li>
                      ))}
                    </ul>
                    {confirmed?.quotationId && (
                      <Link to={`/quotations/${confirmed.quotationId}`} className="font-semibold underline">
                        Open {confirmed.quotationNumber || 'the quotation'}
                      </Link>
                    )}
                  </section>
                );
              })()}

              <div className="flex items-center gap-3">
                <input value={reconcileNote} onChange={(e) => setReconcileNote(e.target.value)}
                  placeholder="Reconciliation note (optional)..."
                  className="flex-1 h-8 rounded-md border border-slate-200 px-3 text-xs focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
                <button
                  disabled={reconcileSaving}
                  onClick={async () => {
                    if (!id) return;
                    setReconcileSaving(true);
                    try {
                      // D81 — only which line, keep/remove and the quantity go to the server;
                      // it rebuilds each line from the stored one.
                      const { changes, blankLines } = quantityChangesFrom(reconcileItems);
                      if (!changes.some((c) => c.keep)) { toast.error('Select at least one item to include.'); return; }
                      if (blankLines.length) {
                        toast.error(`Enter a quantity above 0 for line${blankLines.length === 1 ? '' : 's'} ${blankLines.join(', ')}, or untick ${blankLines.length === 1 ? 'it' : 'them'} to remove ${blankLines.length === 1 ? 'it' : 'them'}.`);
                        return;
                      }
                      const request = { changes, note: reconcileNote, orderUpdatedAt: String(confirmed?.updatedAt || '') };
                      if (reconcileFollowUp) {
                        // D45.3 — the quantities change and the email is marked applied, in one write.
                        try {
                          await applyConfirmedOrderFollowUp(id, reconcileFollowUp._id, request);
                        } catch (applyErr: unknown) {
                          toast.error(followUpErrorMessage(applyErr));
                          return;
                        }
                        setReconcileFollowUp(null);
                      } else {
                        await reconcileConfirmedOrder(id, request);
                      }
                      setReconciling(false);
                      await load();
                    } catch (e: unknown) {
                      toast.error(orderQuantityChangeErrorMessage(e));
                    } finally {
                      setReconcileSaving(false);
                    }
                  }}
                  className="px-5 py-2 rounded-md bg-primary-500 text-white text-xs font-semibold hover:bg-primary-600 disabled:opacity-60"
                >
                  {reconcileSaving ? 'Saving...' : reconcileFollowUp ? 'Apply to order' : 'Finalise Order'}
                </button>
              </div>
            </div>
          )}

          <div className="p-4 border-b border-slate-200">
            <div className="text-xs font-semibold text-slate-700 mb-2">Notes</div>
            <div className="flex items-center gap-2">
              <input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="h-9 flex-1 rounded-md border border-slate-200 px-3 text-xs"
              />
              <button
                onClick={handleSaveNotes}
                disabled={savingNotes}
                className="px-3 py-2 rounded-md bg-white border border-slate-200 text-xs font-semibold text-slate-700 disabled:opacity-60"
              >
                {savingNotes ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>

          {vendorList.length > 0 && (
            <div className="px-4 py-2 text-xs text-slate-600 border-b border-slate-200">
              Vendors on lines: {vendorList.join(', ')}
              <button
                onClick={() => navigate('/vendors')}
                className="ml-3 text-cyan-700 hover:text-cyan-800 underline"
              >
                Vendors directory
              </button>
            </div>
          )}

          {vendorPurchaseOrders.length > 0 && (
            <div className="p-4 border-b border-slate-200 bg-cyan-50/50">
              <div className="text-xs font-semibold text-slate-800 mb-2">Purchase orders (TT$)</div>
              <ul className="space-y-2">
                {vendorPurchaseOrders.map((po: any) => (
                  <li key={po._id} className="flex flex-wrap items-center justify-between gap-2 text-xs">
                    <div>
                      <span className="font-medium text-slate-800">{po.vendorName}</span>
                      <span className="text-slate-500"> · {po.poNumber}</span>
                      {typeof po.totalPrice === 'number' && (
                        <span className="text-slate-700"> · {formatMoney(po.totalPrice, 'TTD')}</span>
                      )}
                      {(po as any).status === 'sent' ? (
                        <span className="ml-2 inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                          Sent
                        </span>
                      ) : (po as any).status === 'amended' ? (
                        // D81: changed since it was sent — needs re-sending.
                        <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                          Amended — re-send
                        </span>
                      ) : (po as any).status === 'cancelled' ? (
                        <span className="ml-2 inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500 line-through">
                          Cancelled
                        </span>
                      ) : (
                        <span className="ml-2 inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">
                          Draft
                        </span>
                      )}
                    </div>
                    <Link
                      to={`/vendor-purchase-orders/${po._id}`}
                      className="text-cyan-700 font-semibold hover:underline"
                    >
                      Open PO
                    </Link>
                  </li>
                ))}
              </ul>

              <div className="mt-3 rounded-md border border-slate-200 p-3">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-xs font-semibold text-slate-700">Send POs to vendors</span>
                  <select
                    value={batchFormat}
                    onChange={(e) => setBatchFormat(e.target.value as 'pdf' | 'excel')}
                    className="text-xs border border-slate-200 rounded px-2 py-1"
                  >
                    <option value="pdf">PDF</option>
                    <option value="excel">Excel</option>
                  </select>
                  <button
                    onClick={() => {
                      // Drafts and amended POs (D81: changed since sent); never a cancelled one, nor one whose email already went (D180).
                      const unsent = (vendorPurchaseOrders || [])
                        .filter((p: any) => (p.status === 'draft' || p.status === 'amended') && !emailedNotMarked.some((row) => row.id === p._id))
                        .map((p: any) => p._id);
                      if (!unsent.length) { toast.info('No unsent POs for this order.'); return; }
                      setBatchResult(null);
                      setEmailPoIds(unsent);
                    }}
                    className="inline-flex items-center gap-2 h-8 px-3 rounded-md bg-primary-500 text-white text-xs font-semibold hover:bg-primary-600 disabled:opacity-60"
                  >
                    Send unsent POs
                  </button>
                </div>
                {batchResult && (
                  <div className="mt-2 text-xs text-slate-600">
                    Sent: <b className="text-emerald-600">{batchResult.sent}</b> · Skipped: <b>{batchResult.skipped}</b> · Failed: <b className="text-rose-600">{batchResult.failed}</b>
                  </div>
                )}
                {emailedNotMarked.length > 0 && (
                  <p className="mt-1 text-xs text-rose-600">
                    {`${emailedNotMarked.map((row) => row.label).join(', ')}: the email went to the vendor, but the purchase order could not be marked as sent. Do not send it again. Left out of Send unsent POs until the page is reloaded; if still unsent then, contact support.`}
                  </p>
                )}
                {emailPoIds && (
                  <VendorPoEmailsDialog
                    poIds={emailPoIds}
                    format={batchFormat}
                    onClose={() => setEmailPoIds(null)}
                    onSent={(result) => {
                      // Failed is the latest send's: a row retried is counted by its new answer (D180).
                      setBatchResult((prev) => ({
                        sent: (prev?.sent ?? 0) + result.sent.length,
                        skipped: (prev?.skipped ?? 0) + result.skipped.length,
                        failed: result.failed.length,
                      }));
                      setEmailedNotMarked((prev) => [...prev, ...result.notRecorded.map((row) => ({ id: row.id, label: row.poNumber || row.id }))]);
                      void load({ quiet: true });
                    }}
                  />
                )}
              </div>
            </div>
          )}

          {/* A9 — totals per tile + overall (D104.21).
              A8 — markup % alert if <20% renders in RED. */}
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50/50">
            <div className="text-xs font-semibold text-slate-700 mb-2">Section Totals & Profitability</div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
              {[
                ...groupedTotals.tiles,
                { ...groupedTotals.overall, key: 'overall', label: 'OVERALL' },
              ].map((b) => {
                const isOverall = b.key === 'overall';
                return (
                  <div
                    key={b.key}
                    className={`rounded-md border px-3 py-2 ${
                      b.alertLowMarkup
                        ? 'border-rose-400 bg-rose-50'
                        : isOverall
                        ? 'border-primary-500 bg-cyan-50/40'
                        : 'border-slate-200 bg-white'
                    }`}
                  >
                    <div className={`text-xs uppercase font-bold mb-1 ${isOverall ? 'text-primary-500' : 'text-slate-600'}`}>
                      {b.label}
                    </div>
                    <div className="space-y-0.5 text-xs text-slate-700">
                      <div>Selling: <span className="font-semibold">{US}{b.selling.toFixed(2)}</span></div>
                      <div>
                        Cost: <span className="font-semibold">{US}{b.cost.toFixed(2)}</span>
                        {/* TECH_DEBT #342 — a null cost (no supplier picked yet, D54) is left
                            out of the sum above, never read as $0. Said here so "Cost" is never
                            mistaken for the whole section's cost when a line is still pending. */}
                        {b.pendingLines > 0 && (
                          <span className="text-amber-700"> (excludes {b.pendingLines} pending line{b.pendingLines === 1 ? '' : 's'})</span>
                        )}
                      </div>
                      <div>Mark-Up: <span className="font-semibold">{US}{b.markupAmount.toFixed(2)}</span></div>
                      <div className={b.alertLowMarkup ? 'text-rose-700 font-bold' : ''}>
                        % vs Cost: {b.pctVsCost === null ? '—' : `${b.pctVsCost.toFixed(1)}%`}
                        {b.alertLowMarkup ? ' ⚠ < 20%' : ''}
                      </div>
                      <div>% vs Selling: {b.pctVsSelling === null ? '—' : `${b.pctVsSelling.toFixed(1)}%`}</div>
                      <div className="text-xs text-slate-500">{b.lines} line{b.lines === 1 ? '' : 's'}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="overflow-x-auto">
            {/* Which line you are on stays on screen while the costing and supplier
                columns scroll (`frozen-cols-*`, index.css). The item name is the third
                column here, and only a leading run can be pinned, so # and UOM are pinned
                with it, at offsets worked out from their widths (D178). */}
            <table
              className="data-table-grid table-fixed text-sm frozen-cols-3 [&_td]:overflow-hidden [&_td]:[overflow-wrap:anywhere]"
              style={{
                ...lineLayout.tableStyle,
                '--frozen-col-2': `${numberWidth}px`,
                '--frozen-col-3': `${numberWidth + uomWidth}px`,
              } as React.CSSProperties}
            >
              <colgroup>
                {lineColumns.map(({ key }) => (
                  <col key={key} style={lineLayout.widths[key] ? { width: lineLayout.widths[key] } : undefined} />
                ))}
              </colgroup>
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs text-slate-600">
                  {lineColumns.map(({ key, header }, at) => (
                    <th key={key} className={`relative overflow-hidden whitespace-nowrap px-4 py-3 ${at < 3 ? 'bg-slate-50' : ''}`}>
                      {header}
                      {key !== 'actions' && (
                        <ColumnResizeGrip
                          onResize={(px) => columnWidths.resize(key, px)}
                          onReset={() => columnWidths.reset(key)}
                          onResizeEnd={columnWidths.commit}
                        />
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {confirmedItems.length === 0 && (
                  <tr>
                    <td colSpan={lineColumns.length} className="px-4 py-6 text-center text-xs text-slate-500">
                      No items found.
                    </td>
                  </tr>
                )}
                {confirmedItems.map((it: any, idx: number) => {
                  // A7 / TECH_DEBT #342 audit — Cost used to be derived from the winning
                  // vendor's quote (vendorOptions[selectedVendor].unitPrice). TECH_DEBT
                  // #204: `vendorOptions` is only ever populated by a quotation-generation
                  // branch gated off by default, so it is empty on nearly every confirmed-
                  // order line and both columns read "--" almost always — the exact same
                  // "looked empty, was actually unreachable" shape as CO-02's dropdown bug.
                  // The line's OWN `unitPrice`/`unitPriceTtd` are the fields a PO is
                  // actually raised from (buildPoLinePrices reads these, not
                  // vendorOptions), carried onto the line by buildConfirmedItemsFromQuotation
                  // / buildConfirmedItemsFromPriceList / changeConfirmedOrderItemSupplier, so
                  // they are what this row reads too.
                  //
                  // D54 (2026-09-22) can leave a line with NEITHER field set — no supplier
                  // picked yet, cost pending, not $0 (TECH_DEBT #342) — so that state is
                  // checked first and short-circuits to Pending before the owner ever sees
                  // a null cost, because `unitCostTtd`'s OWN documented contract (BUSINESS_
                  // RULES#quotation-cost-ttd) is "a line with no cost reads 0" — right for a
                  // historical quotation line, wrong for this one. `unitPriceTtd` (the TT$
                  // pin) needs no rate at all; a non-pinned line needs `ttdToUsd`, which is
                  // null on a price-list-originated order (no linked quotation) — Pending
                  // there too, rather than guessing a rate this page cannot reach.
                  const isPriced = typeof it.unitPrice === 'number' || typeof it.unitPriceTtd === 'number';
                  // D104.10: the US$ cost and the line's cost total are the cost the cards above are
                  // worked from (the exact unit cost; the total rounded once), not the stored cent
                  // cost: a column adding up to more than the card over it would contradict itself.
                  // The TT$ cost is the pin or the stored cost converted, as the PO prints it.
                  const lineCost = orderLineCost(it, quotationItems);
                  const usCost: number | null = typeof it.unitPrice === 'number' && lineCost ? lineCost.unitCost : null;
                  const ttCost: number | null = isPriced ? unitCostTtd(it, ttdToUsd) : null;
                  // Blank beside a line that has a figure: no selling and no cost, or TT$ with no rate (D172.4).
                  const selling = sellingByLine[idx];
                  // Vendor & Date column
                  const supplierAt = it.supplierAssignedAt || it.selectedAt || it.updatedAt || data?.confirmedOrder?.updatedAt;
                  const supplierDate = supplierAt ? formatDate(supplierAt) : '';
                  return (
                  <tr key={idx} className="border-b border-slate-100">
                    <td className="px-4 py-3 bg-white">{idx + 1}</td>
                    {/* The value truncated inside the column rather than allowed to widen it:
                        this column's width is the item name's pin offset. */}
                    <td className="px-4 py-3 bg-white text-slate-700">
                      <div className="truncate" title={it.uom || undefined}>{it.uom || '--'}</div>
                    </td>
                    <td className="px-4 py-3 bg-white text-slate-700">{it.itemName || it.itemDescription || '--'}</td>
                    <td className="px-4 py-3 text-slate-700">{it.quantity || 0}</td>
                    <td className="px-4 py-3 text-slate-700">
                      {usCost != null ? `${US}${exactCostText(usCost)}` : <span className="text-amber-700">Pending</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      {ttCost != null ? `${currencyPrefix('TTD')}${ttCost.toFixed(2)}` : <span className="text-amber-700">Pending</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-700">{formatMoney(lineCost?.totalCost, it.currency)}</td>
                    {show.selling && <td className="px-4 py-3 text-slate-700">{sellMoney(selling.unit, 'USD')}</td>}
                    {show.selling && <td className="px-4 py-3 text-slate-700">{sellMoney(selling.total, 'USD')}</td>}
                    {show.selling && <td className="px-4 py-3 text-slate-700">{sellMoney(selling.unitTtd, 'TTD')}</td>}
                    {show.selling && <td className="px-4 py-3 text-slate-700">{sellMoney(selling.totalTtd, 'TTD')}</td>}
                    <td className="px-4 py-3">
                      {(() => {
                        // Per-item options (with price info) take priority when this line actually
                        // carries embedded vendorOptions; otherwise use THIS item's real live options
                        // (fetched above from the same 4-collection lookup the backend validates
                        // against), never the whole order's vendor list (CO-02).
                        const itemOptions: any[] = Array.isArray(it.vendorOptions) ? it.vendorOptions : [];
                        const liveOptions: any[] = itemSupplierOptions[String(it.itemName || '').trim()] || [];
                        const dropdownVendors: string[] = itemOptions.length >= 2
                          ? itemOptions.map((o: any) => o.vendorName)
                          : liveOptions.map((o: any) => o.vendorName);

                        // D86.5: the stored vendor id names the current vendor; a line without one, by name.
                        const currentVendor = (registeredVendors || []).find((v) => (it.selectedVendorId
                          ? v._id === String(it.selectedVendorId)
                          : v.name.trim().toLowerCase() === String(it.selectedVendor || '').trim().toLowerCase()));

                        const doChange = async (picked: string, pickedVendorId: string | null) => {
                          // The user picks a canonical label; send back a RAW vendorOption name
                          // for this item so the backend's case-insensitive match against the
                          // item's own options accepts it. (Ticket C)
                          const rawForItem =
                            itemOptions.find((o: any) => supplierCanon.canonicalOf(o.vendorName) === picked)?.vendorName ||
                            liveOptions.find((o: any) => supplierCanon.canonicalOf(o.vendorName) === picked)?.vendorName ||
                            picked;
                          // A pick re-prices the line (D105.1), so the vendor the cell already shows
                          // sends nothing. D86.5: the same name is a change only when it is ANOTHER
                          // vendor (two vendors of one name), against the id the picker shows - the
                          // stored one, else (a line stored before the id) the vendor matched by name.
                          const shownVendorId = it.selectedVendorId ? String(it.selectedVendorId) : currentVendor?._id ?? null;
                          if (!rawForItem || (rawForItem === it.selectedVendor && (!pickedVendorId || pickedVendorId === shownVendorId))) return;
                          try {
                            const { poMoves } =
                              await changeConfirmedOrderItemSupplier(id!, idx, rawForItem, pickedVendorId);
                            // Reload, not patch: the move changes purchase orders too (D102.12),
                            // and the PO list and "Send unsent" read them.
                            await load({ quiet: true });
                            // D102.12: which PO lines moved to the new supplier's PO is said.
                            const moved = poMovesMessage(poMoves);
                            if (moved) toast.info(moved, 8000);
                          } catch (err: unknown) {
                            toast.error(changeSupplierErrorMessage(err));
                          }
                        };

                        // Show canonical names only (variants collapsed). The stored value maps
                        // to its canonical for display; picking it normalises the line. (Ticket C)
                        const current = supplierCanon.canonicalOf(it.selectedVendor || '');
                        const canonOptions = supplierCanon.canonicalList(dropdownVendors);
                        const optionFor = (name: string): any =>
                          itemOptions.find((o: any) => supplierCanon.canonicalOf(o.vendorName) === name) ||
                          liveOptions.find((o: any) => supplierCanon.canonicalOf(o.vendorName) === name);
                        // D78 (owner, 2026-09-26): a price the supplier did not print,
                        // worked out from the line total (D75), is offered here too —
                        // `changeConfirmedOrderItemSupplier` re-prices from it — so it
                        // is marked, the same words as the quotation lookup panel.
                        const workedOut = (o: any) => o?.unitPriceFromLineTotal === true;

                        // D105.3 (owner, 2026-10-07): the suppliers with a price for this item
                        // first, each with its price, then every registered vendor (D86's list,
                        // from the same read), searchable. One with no price keeps the cost.
                        // Live options can be priced in TTD as easily as USD (four different
                        // sources — supplierOptions.service.js) — labelled by the owner with the
                        // option's OWN currency: "US$", never a bare "$", and no label when none
                        // is stated (CLAUDE.md invariant 5; BUSINESS_RULES#quotation-screen-money-labels).
                        const priced = canonOptions.map((name: string) => {
                          const opt = optionFor(name);
                          return {
                            name,
                            // D170.9: the vendor the option is filed under; the label may be a variant of its name.
                            vendorId: opt?.vendorId ?? null,
                            note: opt?.unitPrice == null
                              ? undefined
                              : `${currencyPrefix(opt.currency)}${Number(opt.unitPrice).toFixed(2)}${
                                  workedOut(opt) ? ' (worked out from line total)' : ''
                                }`,
                          };
                        });

                        return (
                          <div className="flex flex-col gap-0.5">
                            <VendorSelect
                              compact
                              allowClear={false}
                              ariaLabel={`Supplier for ${it.itemName || it.itemDescription || `line ${idx + 1}`}`}
                              placeholder="Pick a supplier…"
                              className="max-w-[200px]"
                              value={{ vendorId: currentVendor ? currentVendor._id : null, name: current }}
                              vendors={registeredVendors}
                              priced={priced}
                              onChange={(next) => doChange(next.name, next.vendorId)}
                            />
                            {itemSupplierUnread[String(it.itemName || '').trim()] === null && (
                              <span role="status" className="text-xs text-amber-800">Could not read suppliers</span>
                            )}
                            <SupplierSourcesNote sources={itemSupplierUnread[String(it.itemName || '').trim()] ?? []} />
                            {supplierDate && <span className="text-xs text-slate-500">{supplierDate}</span>}
                            {current && workedOut(optionFor(current)) && (
                              <span
                                className="text-xs leading-[13px] font-semibold text-amber-700"
                                title="The supplier printed no unit price. It was worked out by dividing the line total by the quantity. Check it against the invoice before using it."
                              >
                                worked out from line total
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    {/* PO Status — send-state of the PO for this item's vendor */}
                    <td className="px-4 py-3">
                      {(() => {
                        const badge = PO_STATUS_BADGE[itemPoSendStatus(it.selectedVendor || '', vendorPurchaseOrders as any[], supplierCanon.canonicalOf)];
                        return (
                          <span className={`inline-block px-2 py-1 rounded-md border text-xs font-semibold whitespace-nowrap ${badge.className}`}>
                            {badge.label}
                          </span>
                        );
                      })()}
                    </td>
                    {show.category && <td className="px-4 py-3 text-slate-700">{it.category || '--'}</td>}
                    {show.remarks && <td className="px-4 py-3 text-slate-700">{it.remarks || ''}</td>}
                    {/* D172.3 (29 Oct call, Deborah): staff-only internal remark in its own column. */}
                    {show.internalRemarks && <td className="px-4 py-3 text-slate-700">{it.internalRemarks || ''}</td>}
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleDeleteItem(idx)}
                        className="text-xs text-rose-600 hover:text-rose-700"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showStockProposal && id && (
        <ProcurementProposalModal
          confirmedOrderId={id}
          currency={confirmed?.currency}
          onCancel={() => setShowStockProposal(false)}
          onConfirm={async (allocations) => {
            setShowStockProposal(false);
            await handleCreatePo(false, allocations);
          }}
        />
      )}
    </div>
  );
};
